import { FormGroupProps } from "../../form-types";
import {
	CountrySelectShadcn,
} from "../selects/country-select/country.select";
import {
	PostalCodeAndCityInputShadcn,
} from "@/dentlab/src/components/postal-code-and-city-input/postal-code-and-city-input.component";
import { CustomTextFieldShadcn } from "../custom-input-components/custom-text-field-shadcn.component";
import { cn } from "@/dentlab/src/lib/util";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { getLabCountry } from "@/dentlab/src/lib/utils/country-utils";

type AddressGroupFieldTypes =
	| "street"
	| "city"
	| "postal_code"
	| "country"
	| "email"
	| "fax"
	| "phone_personal"
	| "phone_business"
	| "phone_mobile"
	| "additional_information";

interface AddressGroupProps extends FormGroupProps {
	onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	exclude?: AddressGroupFieldTypes[];
	requiredFields?: AddressGroupFieldTypes[];
}

export const AddressGroupShadcn: React.FC<
	AddressGroupProps & {
		className?: string;
	}
> = ({
	formData,
	formErrors,
	onChange,
	onValueChange = () => {},
	customNaming = null,
	exclude = [],
	requiredFields = [],
	className,
}) => {
	const labCountry = useCentralStore((state) => getLabCountry(state.organization));

	return (
		<div className={cn("flex flex-col gap-1", className)}>
			<CustomTextFieldShadcn
				label="Strasse"
				name={customNaming?.street || "street"}
				value={
					customNaming?.street
						? formData[customNaming.street]
						: (formData.street ?? "")
				}
				onChange={onChange}
				required={requiredFields.includes("street")}
				validationError={formErrors?.street}
			/>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<div className="w-[50%]">
					{!exclude.includes("country") && (
						<CountrySelectShadcn
							onChange={(value) =>
								onValueChange({
									name: customNaming?.country || "country",
									value: value,
								})
							}
							value={
								customNaming?.country
									? formData[customNaming.country]
									: (formData.country ?? "")
							}
							required={requiredFields.includes("country")}
						/>
					)}
				</div>
				<div className="w-[50%]">
					<PostalCodeAndCityInputShadcn
						country={
							customNaming?.country
								? formData[customNaming.country]
								: (formData.country ?? labCountry)
						}
						postalCodeValue={
							customNaming?.postal_code
								? formData[customNaming.postal_code]
								: (formData.postal_code ?? "")
						}
						cityValue={
							customNaming?.city
								? formData[customNaming.city]
								: (formData.city ?? "")
						}
						required={
							requiredFields.includes("city") ||
							requiredFields.includes("postal_code")
						}
						onPostalCodeChange={(value) =>
							onValueChange({
								name:
									customNaming?.postal_code || "postal_code",
								value: value,
							})
						}
						onCityChange={(value) =>
							onValueChange({
								name: customNaming?.city || "city",
								value: value,
							})
						}
					/>
				</div>
			</div>

			<CustomTextFieldShadcn
				label="Zusatz"
				name={
					customNaming?.additional_information ||
					"additional_information"
				}
				value={
					customNaming?.additional_information
						? formData[customNaming.additional_information]
						: (formData.additional_information ?? "")
				}
				onChange={onChange}
				required={requiredFields.includes("additional_information")}
				validationError={formErrors?.additional_information}
			/>

			{!exclude.includes("phone_personal") && (
				<CustomTextFieldShadcn
					id="phone_personal"
					label="Telefon (privat)"
					name={customNaming?.phone_personal || "phone_personal"}
					value={
						customNaming?.phone_personal
							? formData[customNaming.phone_personal]
							: (formData.phone_personal ?? "")
					}
					onChange={onChange}
					required={requiredFields.includes("phone_personal")}
					validationError={formErrors?.phone_personal}
				/>
			)}
			{!exclude.includes("phone_business") && (
				<CustomTextFieldShadcn
					id="phone_business"
					label="Telefon (geschäftlich)"
					name={customNaming?.phone_business || "phone_business"}
					value={
						customNaming?.phone_business
							? formData[customNaming.phone_business]
							: (formData.phone_business ?? "")
					}
					onChange={onChange}
					required={requiredFields.includes("phone_business")}
					validationError={formErrors?.phone_business}
				/>
			)}
			{!exclude.includes("phone_mobile") && (
				<CustomTextFieldShadcn
					id="phone_mobile"
					label="Telefon (mobil)"
					name={customNaming?.phone_mobile || "phone_mobile"}
					value={
						customNaming?.phone_mobile
							? formData[customNaming.phone_mobile]
							: (formData.phone_mobile ?? "")
					}
					onChange={onChange}
					required={requiredFields.includes("phone_mobile")}
					validationError={formErrors?.phone_mobile}
				/>
			)}
			{!exclude.includes("email") && (
				<CustomTextFieldShadcn
					id="email"
					label="E-Mail"
					name={customNaming?.email || "email"}
					value={
						customNaming?.email
							? formData[customNaming.email]
							: (formData.email ?? "")
					}
					onChange={onChange}
					required={requiredFields.includes("email")}
					validationError={formErrors?.email}
				/>
			)}
			{!exclude.includes("fax") && (
				<CustomTextFieldShadcn
					label="Fax"
					name={customNaming?.fax || "fax"}
					value={
						customNaming?.fax
							? formData[customNaming.fax]
							: (formData.fax ?? "")
					}
					onChange={onChange}
					required={requiredFields.includes("fax")}
					validationError={formErrors?.fax}
				/>
			)}
		</div>
	);
};
